import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import Blog from './pages/Blog';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Roadmap from './pages/Roadmap';
// import Privacy from './pages/Privacy';
import { Share2, ExternalLink, Video, Mail, Upload, Download } from 'lucide-react';
import './App.css';

function App() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [filmResults, setFilmResults] = useState(null); // Array of 3 results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videoLink, setVideoLink] = useState('');
  const [uploadType, setUploadType] = useState('file'); // 'file', 'link', 'record'
  const [history, setHistory] = useState([]);
  const [multipleMovies, setMultipleMovies] = useState(false);

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [webcamStream, setWebcamStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const webcamVideoRef = React.useRef(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState({ 1: true }); // Object to track which result's details are open
  const [startSeconds, setStartSeconds] = useState('');
  const [endSeconds, setEndSeconds] = useState('');
  const [showSupportText, setShowSupportText] = useState({
    patreon: false,
    kofi: false
  });
  const [showTooltip, setShowTooltip] = useState({
    patreon: false,
    kofi: false,
    kofi: false,
    search: false,
    share: {}
  });
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatSent, setChatSent] = useState(false);

  const loadingTexts = [
    "Analyzing video...",
    "Searching for similar frames...",
    "Almost ready...",
    "This may take a few seconds...",
    "Magic is happening....",
    "Comparing frames with films...",
    "Processing the video stream...",
    "Scanning for key points...",
    "Identifying visual features...",
    "Looking for potential matches...",
    "Extracting scene data...",
    "Loading necessary components...",
    "Reviewing historical data...",
    "Getting everything in order...",
    "Running final checks...",
    "Just a moment...",
    "Preparing the results...",
    "Synchronizing data sources...",
    "Validating the findings...",
    "Piecing the information together...",
    "Working on your request...",
    "Almost there..."
  ];

  const [currentLoadingText, setCurrentLoadingText] = useState(loadingTexts[0]);

  useEffect(() => {
    const savedHistory = localStorage.getItem('searchHistory');
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    let interval;
    if (loading) {
      let index = 0;
      interval = setInterval(() => {
        index = (index + 1) % loadingTexts.length;
        setCurrentLoadingText(loadingTexts[index]);
      }, 2000); // змінюємо текст кожні 2 секунди
    }
    return () => clearInterval(interval);
  }, [loading]);

  const saveToHistory = (results) => {
    // Save the top result (highest confidence) to history
    if (results && results.length > 0) {
      const topResult = results[0];
      const newHistory = [{ filmName: topResult.name, timestamp: new Date().toISOString(), fullResult: topResult }, ...history].slice(0, 40);
      setHistory(newHistory);
      localStorage.setItem('searchHistory', JSON.stringify(newHistory));
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFilmResults(null);
      setError(null);
      if (file.type.startsWith('video/')) {
        extractFramesAndUpload(file);
      } else {
        uploadVideo(file);
      }
    }
  };

  const handleLinkSubmit = async (e) => {
    e.preventDefault();
    if (!videoLink) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/process-video-clip?byUrl=true', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: videoLink,
          startSeconds: startSeconds ? parseInt(startSeconds) : undefined,
          endSeconds: endSeconds ? parseInt(endSeconds) : undefined
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error on video upload');
      }

      if (!data.filmData.includes('Not found, please try another clip')) {
        const parsedData = JSON.parse(data.filmData);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          setFilmResults(parsedData);
          saveToHistory(parsedData);
          // Initialize details open state for each result
          const detailsState = {};
          parsedData.forEach((_, index) => {
            detailsState[index] = false;
          });
          setIsDetailsOpen(detailsState);
        } else if (parsedData.includes && !parsedData.includes('Not found, please try another clip')) {
          // Fallback for old format (single result)
          setFilmResults([parsedData]);
          saveToHistory([parsedData]);
          setIsDetailsOpen({ 0: false });
        } else {
          setFilmResults(null);
          setShowTooltip(prev => ({ ...prev, search: true }));
          setTimeout(() => {
            setShowTooltip(prev => ({ ...prev, search: false }));
          }, 5000);
        }
      } else {
        setFilmResults(null);
        setShowTooltip(prev => ({ ...prev, search: true }));
        setTimeout(() => {
          setShowTooltip(prev => ({ ...prev, search: false }));
        }, 5000);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error on video upload');
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setFilmResults(null);
    setStartSeconds('');
    setEndSeconds('');
    setVideoLink('');
    setError(null);
    setIsDetailsOpen({});
    setFeedbackSent(false);
  };

  const sendFeedback = async (isPositive) => {
    if (feedbackSent) return;

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isPositive: isPositive,
          filmResults: filmResults,
          uploadType: uploadType,
          videoLink: uploadType === 'link' ? videoLink : null,
        }),
      });

      if (response.ok) {
        setFeedbackSent(true);
      }
    } catch (err) {
      console.error('Error sending feedback:', err);
    }
  };

  const uploadVideo = async (file) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('video', file);

      const response = await fetch('/api/process-video-clip', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error on video upload');
      }

      if (!data.filmData.includes('Not found, please try another clip')) {
        const parsedData = JSON.parse(data.filmData);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          setFilmResults(parsedData);
          saveToHistory(parsedData);
          // Initialize details open state for each result
          const detailsState = {};
          parsedData.forEach((_, index) => {
            detailsState[index] = false;
          });
          setIsDetailsOpen(detailsState);
        } else if (parsedData.includes && !parsedData.includes('Not found, please try another clip')) {
          // Fallback for old format (single result)
          setFilmResults([parsedData]);
          saveToHistory([parsedData]);
          setIsDetailsOpen({ 0: false });
        } else {
          setFilmResults(null);
          setShowTooltip(prev => ({ ...prev, search: true }));
          setTimeout(() => {
            setShowTooltip(prev => ({ ...prev, search: false }));
          }, 5000);
        }
      } else {
        setFilmResults(null);
        setShowTooltip(prev => ({ ...prev, search: true }));
        setTimeout(() => {
          setShowTooltip(prev => ({ ...prev, search: false }));
        }, 5000);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error on video upload');
    } finally {
      setLoading(false);
    }
  };

  const extractFramesAndUpload = async (file) => {
    setLoading(true);
    setError(null);

    try {
      // Create a video element to decode frames
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.src = URL.createObjectURL(file);

      await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          if (video.duration === Infinity) {
            video.currentTime = 1e101;
            video.onseeked = () => {
              video.onseeked = null;
              video.currentTime = 0;
              resolve();
            };
          } else {
            resolve();
          }
        };
        video.onerror = () => reject(new Error('Failed to load video'));
      });

      const duration = Math.floor(video.duration || 0);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Seek and capture at approximately 1 FPS
      const timestamps = [];
      for (let t = 0; t <= duration; t += 1) {
        timestamps.push(t);
      }

      // Limit max frames to avoid huge payloads (e.g., 60 frames)
      const MAX_FRAMES = 60;
      const step = Math.max(1, Math.ceil(timestamps.length / MAX_FRAMES));
      const sampled = timestamps.filter((_, i) => i % step === 0);

      const frames = [];
      for (const t of sampled) {
        await new Promise((resolve) => {
          const targetTime = Math.min(t, Math.max(0, duration - 0.01));
          let timeoutId;

          const captureFrame = () => {
            clearTimeout(timeoutId);
            try {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
              frames.push(dataUrl);
            } catch (e) { }
            resolve();
          };

          if (Math.abs(video.currentTime - targetTime) < 0.001) {
            captureFrame();
          } else {
            const onSeeked = () => {
              video.removeEventListener('seeked', onSeeked);
              captureFrame();
            };
            video.addEventListener('seeked', onSeeked);
            video.currentTime = targetTime;

            // Fallback: If seeked doesn't fire (e.g., mkv missing index), force capture
            timeoutId = setTimeout(() => {
              video.removeEventListener('seeked', onSeeked);
              captureFrame();
            }, 500);
          }
        });
      }

      const endpoint = multipleMovies ? '/api/process-frames/multiple-films' : '/api/process-frames';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: frames })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Error on frames upload');
      }

      if (!data.filmData.includes('Not found, please try another clip')) {
        const parsedData = JSON.parse(data.filmData);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          setFilmResults(parsedData);
          saveToHistory(parsedData);
          // Initialize details open state for each result
          const detailsState = {};
          parsedData.forEach((_, index) => {
            detailsState[index] = false;
          });
          setIsDetailsOpen(detailsState);
        } else if (parsedData.includes && !parsedData.includes('Not found, please try another clip')) {
          // Fallback for old format (single result)
          setFilmResults([parsedData]);
          saveToHistory([parsedData]);
          setIsDetailsOpen({ 0: false });
        } else {
          setFilmResults(null);
          setShowTooltip(prev => ({ ...prev, search: true }));
          setTimeout(() => { setShowTooltip(prev => ({ ...prev, search: false })); }, 5000);
        }
      } else {
        setFilmResults(null);
        setShowTooltip(prev => ({ ...prev, search: true }));
        setTimeout(() => { setShowTooltip(prev => ({ ...prev, search: false })); }, 5000);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error while processing video');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, buttonType, originalIndex = null) => {
    navigator.clipboard.writeText(text);

    if (buttonType === 'share' && originalIndex !== null) {
      setShowTooltip(prev => ({
        ...prev,
        share: { ...prev.share, [originalIndex]: true }
      }));
      setTimeout(() => {
        setShowTooltip(prev => ({
          ...prev,
          share: { ...prev.share, [originalIndex]: false }
        }));
      }, 2000);
    } else {
      setShowTooltip(prev => ({ ...prev, [buttonType]: true }));
      setTimeout(() => {
        setShowTooltip(prev => ({ ...prev, [buttonType]: false }));
      }, 2000);
    }
  };

  const generateWatchLink = (platform, filmName) => {
    const encodedName = encodeURIComponent(filmName);
    switch (platform) {
      case 'youtube':
        return `https://www.youtube.com/results?search_query=${encodedName}+movie`;
      case 'amazon':
        return `https://www.amazon.com/s?k=${encodedName}&i=movies-tv`;
      case 'appletv':
        return `https://tv.apple.com/us/search?term=${encodedName}`;
      case 'fandango':
        return `https://www.fandango.com/search?q=${encodedName}`;
      default:
        return '#';
    }
  };

  const createRipple = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.className = 'ripple';
    button.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove());
  };

  const handleImportHistory = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (Array.isArray(imported)) {
          const merged = [...imported, ...history];
          const unique = merged.filter((item, idx, arr) =>
            arr.findIndex(i => i.filmName === item.filmName && i.timestamp === item.timestamp) === idx
          ).slice(0, 100);
          setHistory(unique);
          localStorage.setItem('searchHistory', JSON.stringify(unique));
        }
      } catch (err) {
        console.error('Failed to import history:', err);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    setLoading(true);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      const response = await fetch('/api/search-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: chatMessage
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error on text search');
      }

      if (data.filmData && !data.filmData.includes('Not found')) {
        const parsedData = JSON.parse(data.filmData);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          setFilmResults(parsedData);
          saveToHistory(parsedData);
          const detailsState = {};
          parsedData.forEach((_, index) => {
            detailsState[index] = false;
          });
          setIsDetailsOpen(detailsState);
        } else if (parsedData.name) {
          setFilmResults([parsedData]);
          saveToHistory([parsedData]);
          setIsDetailsOpen({ 0: false });
        } else {
          setFilmResults(null);
          setShowTooltip(prev => ({ ...prev, search: true }));
          setTimeout(() => { setShowTooltip(prev => ({ ...prev, search: false })); }, 5000);
        }
      } else {
        setFilmResults(null);
        setShowTooltip(prev => ({ ...prev, search: true }));
        setTimeout(() => { setShowTooltip(prev => ({ ...prev, search: false })); }, 5000);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error on text search');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (film, index) => {
    const shareText = `Check out this movie I found using movie-identifier.com!\n\n🎬 ${film.name} (${film.year})\n👤 Director: ${film.director}\n🎭 Actors: ${film.actors}\n⭐ Confidence: ${film.confidence}%`;
    handleCopy(shareText, 'share', index);
  };

  const getSupportedMimeType = () => {
    const types = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'];
    return types.find(type => MediaRecorder.isTypeSupported(type)) || '';
  };

  const isMobileDevice = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const startRecording = async () => {
    try {
      let stream;
      let cameraStream = null;
      const mobile = isMobileDevice();

      if (!mobile && navigator.mediaDevices.getDisplayMedia) {
        // Desktop: screen capture
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true
        });
      } else {
        // Mobile or no getDisplayMedia: use camera (rear-facing)
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: true
        });
      }
      setScreenStream(stream);

      // Webcam PIP - optional, desktop only
      if (!mobile) {
        try {
          cameraStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false
          });
          setWebcamStream(cameraStream);
          if (webcamVideoRef.current) {
            webcamVideoRef.current.srcObject = cameraStream;
          }
        } catch (camErr) {
          console.warn('Webcam not available for PIP:', camErr);
        }
      }

      // Auto-detect supported mimeType with fallback chain
      let recorder;
      const mimeType = getSupportedMimeType();
      try {
        recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      } catch (e1) {
        console.warn('MediaRecorder failed with', mimeType, '- trying video/mp4');
        try {
          recorder = new MediaRecorder(stream, { mimeType: 'video/mp4' });
        } catch (e2) {
          console.warn('MediaRecorder failed with video/mp4 - using default');
          recorder = new MediaRecorder(stream);
        }
      }
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        const actualType = recorder.mimeType || 'video/webm';
        const ext = actualType.includes('mp4') ? 'mp4' : 'webm';
        const blob = new Blob(chunks, { type: actualType });
        const file = new File([blob], `recorded_screen.${ext}`, { type: actualType });

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        if (cameraStream) {
          cameraStream.getTracks().forEach(track => track.stop());
        }

        setScreenStream(null);
        setWebcamStream(null);
        setIsRecording(false);
        setMediaRecorder(null);

        // Upload recorded file
        setStartSeconds('');
        setEndSeconds('');
        extractFramesAndUpload(file);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);

      // Handle user stopping via browser UI (desktop screen share)
      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state === 'recording') {
          recorder.stop();
        }
      };

    } catch (err) {
      console.error("Error starting recording:", err);
      // Provide specific error messages
      if (err.name === 'NotAllowedError') {
        setError("Permission denied. Please allow screen/camera access and try again.");
      } else if (err.name === 'NotFoundError') {
        setError("No camera or screen capture device found.");
      } else if (err.name === 'NotSupportedError' || err.name === 'TypeError') {
        setError("Screen recording is not supported in this browser. Please try Chrome or Edge.");
      } else if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        setError("Screen recording requires HTTPS. Please use a secure connection.");
      } else {
        setError("Failed to start recording. Please check permissions and try again.");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    }
  };

  const MainContent = () => (
    <div className="content-wrapper">
      {history.length > 0 && (
        <div className='empty-main-block'></div>
      )}
      <div className="main-content">
        <div className='section-1'>
          <h2 className="main-heading">Movie Identifier</h2>
          <h2 className="sub-heading">Find any movie from video clip or link</h2>
        </div>

        {(!filmResults && !error) && (
          <div className='section-2'>
            <div className="toggle-container">
              <div
                className={`toggle-switch ${uploadType === 'link' ? 'active-link' : uploadType === 'record' ? 'active-record' : ''}`}
                style={{ gridTemplateColumns: '1fr 1fr 1fr' }} // Override to 3 columns
              >
                <button
                  className={uploadType === 'file' ? 'active' : ''}
                  onClick={() => setUploadType('file')}
                >
                  Upload File
                </button>
                <button
                  className={uploadType === 'link' ? 'active' : ''}
                  onClick={() => setUploadType('link')}
                >
                  Paste Link
                </button>
                <button
                  className={uploadType === 'record' ? 'active' : ''}
                  onClick={() => setUploadType('record')}
                >
                  Record
                </button>
                <div className="slider" style={{
                  width: '33.33%',
                  left: uploadType === 'file' ? '0' : uploadType === 'link' ? '33.33%' : '66.66%'
                }}></div>
              </div>
            </div>

            {uploadType === 'file' ? (
              <div className="upload-section">
                <input
                  type="file"
                  accept="video/*,image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="video-input"
                  disabled={loading}
                />
                <label htmlFor="video-input" className="upload-button btn-3d" onClick={createRipple}>
                  {loading ? 'Processing...' : 'Choose video or image'}
                </label>
                <div style={{ marginTop: '15px', display: 'flex', alignItems: 'end', justifyContent: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="multiple-movies"
                    checked={multipleMovies}
                    onChange={(e) => setMultipleMovies(e.target.checked)}
                    disabled={loading}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="multiple-movies" style={{ cursor: 'pointer', fontSize: '15px', marginTop: '1rem' }}>
                    More than one movie in the videoclip
                  </label>
                </div>
              </div>
            ) : null}

            {uploadType === 'link' && (
              <>
                <span className='experimental-text'>(Experimental)</span>
                <form onSubmit={handleLinkSubmit} className="link-form">
                  <div className="link-input-group">
                    <input
                      type="url"
                      value={videoLink}
                      onChange={(e) => setVideoLink(e.target.value)}
                      placeholder="Paste link to Shorts or Reels"
                      className="link-input"
                    />
                    <div className="time-inputs">
                      <div className="time-input-wrapper">
                        <input
                          type="number"
                          value={startSeconds}
                          onChange={(e) => setStartSeconds(e.target.value)}
                          placeholder="Start at (seconds)"
                          className="time-input"
                          min="0"
                        />
                      </div>
                      <div className="time-input-wrapper">
                        <input
                          type="number"
                          value={endSeconds}
                          onChange={(e) => setEndSeconds(e.target.value)}
                          placeholder="End at (seconds)"
                          className="time-input"
                          min="0"
                          disabled={!startSeconds}
                          title={!startSeconds ? "Ending seconds can be added only with starting seconds" : ""}
                        />
                        {!startSeconds && (
                          <div className="tooltip">
                            Ending seconds can be added only with starting seconds
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="submit-button btn-3d" disabled={loading} onClick={createRipple}>
                    {loading ? 'Processing...' : 'Search'}
                  </button>
                </form>
              </>
            )}

            {uploadType === 'record' && (
              <div className="record-section" style={{ textAlign: 'center', padding: '20px' }}>
                <p style={{ marginBottom: '20px', color: 'var(--text-muted)' }}>
                  {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
                    ? 'Point your camera at the screen playing the movie.'
                    : 'Record your screen to identify a movie.'}
                </p>
                {!isRecording ? (
                  <button
                    onClick={(e) => { createRipple(e); startRecording(); }}
                    className="upload-button btn-3d"
                    style={{ background: '#e74c3c' }}
                    disabled={loading}
                  >
                    <Video size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Start Recording
                  </button>
                ) : (
                  <button
                    onClick={(e) => { createRipple(e); stopRecording(); }}
                    className="upload-button btn-3d"
                    style={{ background: '#2ecc71' }}
                    disabled={loading}
                  >
                    Stop & Process
                  </button>
                )}

                <div style={{ marginTop: '15px', display: 'flex', alignItems: 'end', justifyContent: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="multiple-movies"
                    checked={multipleMovies}
                    onChange={(e) => setMultipleMovies(e.target.checked)}
                    disabled={loading}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="multiple-movies" style={{ cursor: 'pointer', fontSize: '15px', marginTop: '1rem' }}>
                    More than one movie in the videoclip
                  </label>
                </div>

                {/* Floating Webcam PIP */}
                <div style={{
                  display: webcamStream ? 'block' : 'none',
                  position: 'fixed',
                  bottom: '20px',
                  right: '20px',
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                  border: '4px solid var(--primary-color)',
                  zIndex: 9999,
                  backgroundColor: '#000'
                }}>
                  <video
                    ref={webcamVideoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: 'scaleX(-1)' // Mirror the webcam
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">{currentLoadingText}</p>
          </div>
        )}

        {showTooltip.search && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#333',
            color: '#fff',
            padding: '15px 20px',
            borderRadius: '8px',
            fontSize: '16px',
            zIndex: 1000,
            textAlign: 'center',
            maxWidth: '80%',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
          }}>
            We are informed about the {uploadType === 'file' ? 'video file' : 'link'} that couldn't be found.
          </div>
        )}

        {filmResults && filmResults.length > 0 && (
          <div className="result-container">
            <h3 className="result-heading">Found Movies:</h3>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: multipleMovies ? 'center' : 'flex-start',
              gap: '20px',
              flexWrap: 'wrap',
              marginBottom: '20px',
              flexDirection: multipleMovies ? 'column' : 'row'
            }}>
              {/* Desktop: [1, 0, 2] - second on left, first in center, third on right */}
              {/* Mobile: [0, 1, 2] - first, second, third from top to bottom */}
              {(multipleMovies ? filmResults : [filmResults[1], filmResults[0], filmResults[2]]).map((film, displayIndex) => {
                if (!film) return null;
                // Original index: 1 -> 0, 0 -> 1, 2 -> 2
                const originalIndex = multipleMovies ? displayIndex : (displayIndex === 0 ? 1 : displayIndex === 1 ? 0 : 2);
                const isCenter = multipleMovies ? false : displayIndex === 1; // First result (highest confidence) in center

                return (
                  <div
                    key={originalIndex}
                    className="film-result-item"
                    data-original-index={originalIndex}
                    style={{
                      flex: isCenter ? '0 0 320px' : (multipleMovies ? '0 0 auto' : '0 0 280px'),
                      width: multipleMovies ? '100%' : 'auto',
                      padding: isCenter ? '20px' : '15px',
                      paddingTop: '5px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: isCenter ? '#f9f9f9' : '#fff',
                      boxShadow: isCenter ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 6px rgba(0,0,0,0.1)',
                      transform: isCenter ? 'scale(1.05)' : 'scale(1)',
                      transition: 'transform 0.3s ease',
                      maxWidth: multipleMovies ? '600px' : (isCenter ? '320px' : '280px'),
                      margin: multipleMovies ? '0 auto' : '0'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                      <p className="film-name" style={{
                        margin: 0,
                        fontWeight: isCenter ? 'bold' : 'normal',
                        fontSize: isCenter ? '18px' : '16px',
                        flex: '1 1 100%'
                      }}>
                        {originalIndex + 1}. {film.name}
                      </p>
                      <span style={{
                        padding: '5px 12px',
                        backgroundColor: originalIndex === 0 ? '#4CAF50' : originalIndex === 1 ? '#FF9800' : '#9E9E9E',
                        color: '#fff',
                        borderRadius: '15px',
                        fontSize: isCenter ? '15px' : '14px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}>
                        {film.confidence}% chance
                      </span>
                    </div>

                    <div style={{ position: 'relative', textAlign: 'right', marginBottom: '10px' }}>
                      <button
                        onClick={() => handleShare(film, originalIndex)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          marginLeft: 'auto',
                          fontSize: '14px'
                        }}
                        title="Share Result"
                      >
                        <Share2 size={16} /> Share
                      </button>

                      {showTooltip.share[originalIndex] && (
                        <div style={{
                          position: 'absolute',
                          top: '-30px',
                          right: '0',
                          backgroundColor: 'var(--tooltip-bg)',
                          color: 'var(--tooltip-text)',
                          padding: '5px 10px',
                          borderRadius: '5px',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          zIndex: 10
                        }}>
                          Copied to clipboard!
                        </div>
                      )}
                    </div>

                    <div className="film-details-wrapper">
                      {film.imageUrl && (
                        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                          <img src={film.imageUrl} alt={`${film.name} poster`} style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', objectFit: 'contain' }} />
                        </div>
                      )}
                      <button
                        className="details-toggle"
                        onClick={() => setIsDetailsOpen(prev => ({ ...prev, [originalIndex]: !prev[originalIndex] }))}
                      >
                        <span className='details-text'>Details</span>
                        <span className={`arrow ${isDetailsOpen[originalIndex] ? '' : 'open'}`}>▼</span>
                      </button>
                      <div className={`film-details ${isDetailsOpen[originalIndex] ? '' : 'open'}`}>
                        <p><strong>Director:</strong> {film.director}</p>
                        <p><strong>Actors:</strong> {film.actors}</p>
                        <p><strong>Year:</strong> {film.year}</p>
                        <p><strong>Country:</strong> {film.country}</p>
                        <p><strong>Genre:</strong> {film.genre}</p>

                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid var(--border-light)' }}>
                          <p style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>Watch On:</p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            <a href={generateWatchLink('youtube', film.name)} target="_blank" rel="noopener noreferrer" className="watch-link btn-3d">
                              YouTube <ExternalLink size={12} />
                            </a>
                            <a href={generateWatchLink('amazon', film.name)} target="_blank" rel="noopener noreferrer" className="watch-link btn-3d">
                              Amazon <ExternalLink size={12} />
                            </a>
                            <a href={generateWatchLink('appletv', film.name)} target="_blank" rel="noopener noreferrer" className="watch-link btn-3d">
                              Apple TV <ExternalLink size={12} />
                            </a>
                            <a href={generateWatchLink('fandango', film.name)} target="_blank" rel="noopener noreferrer" className="watch-link btn-3d">
                              Fandango <ExternalLink size={12} />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{
              marginTop: '20px',
              textAlign: 'center',
              padding: '20px'
            }}>
              <p style={{
                marginBottom: '15px',
                fontSize: '16px',
                fontWeight: '500'
              }}>
                Has the result been found?
              </p>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '15px',
                alignItems: 'center'
              }}>
                <button
                  onClick={() => sendFeedback(true)}
                  disabled={feedbackSent}
                  className={`feedback-btn feedback-yes btn-3d ${feedbackSent ? 'feedback-disabled' : ''}`}
                  title="Yes, result found"
                >
                  👍
                </button>
                <button
                  onClick={() => sendFeedback(false)}
                  disabled={feedbackSent}
                  className={`feedback-btn feedback-no btn-3d ${feedbackSent ? 'feedback-disabled' : ''}`}
                  title="No, result not found"
                >
                  👎
                </button>
              </div>
              {feedbackSent && (
                <p style={{
                  marginTop: '10px',
                  fontSize: '14px',
                  color: '#666',
                  fontStyle: 'italic'
                }}>
                  Thank you for your feedback!
                </p>
              )}
            </div>
            <button onClick={(e) => { createRipple(e); resetState(); }} className="try-again-button btn-3d">
              Try Another Search
            </button>
          </div>
        )}

        {error && (
          <>
            <div className="error-container">
              <p className="error-text">{error}</p>
            </div>
            <button onClick={(e) => { createRipple(e); resetState(); }} className="try-again-button-error btn-3d">
              Try Another Search
            </button>
          </>
        )}
      </div>

      {history.length > 0 && (
        <div className="history-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', gap: '8px' }}>
            <h3>Recent Searches</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => {
                  const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(history, null, 2));
                  const downloadAnchorNode = document.createElement('a');
                  downloadAnchorNode.setAttribute("href", dataStr);
                  downloadAnchorNode.setAttribute("download", "videoshazam_history.json");
                  document.body.appendChild(downloadAnchorNode);
                  downloadAnchorNode.click();
                  downloadAnchorNode.remove();
                }}
                className="history-action-btn btn-3d"
                title="Download History as JSON"
              >
                <Download size={14} /> Export
              </button>
              <label className="history-action-btn btn-3d" title="Import History from JSON" style={{ cursor: 'pointer' }}>
                <Upload size={14} /> Import
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportHistory}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
          <div className="history-list">
            {history.map((item, index) => (
              <div
                key={index}
                className="history-item"
                onClick={() => {
                  if (item.fullResult) {
                    setFilmResults([item.fullResult]);
                    setIsDetailsOpen({ 0: false });
                  }
                }}
                style={{ cursor: item.fullResult ? 'pointer' : 'default' }}
                title={item.fullResult ? "Click to view full details" : ""}
              >
                <span>{item.filmName}</span>
                <span className="history-date">
                  {new Date(item.timestamp).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const mediaStyle = {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    margin: 0,
    padding: 0,
    objectFit: 'cover',
    transform: 'none',
    left: 0,
    top: 0,
    position: 'relative',
  };

  const supportBtnClasses = `support-button patreon-button ${showSupportText.patreon ? 'qr-shown' : 'btn-3d'}`

  return (
    <Router>
      <div className="container">
        <Navigation theme={theme} toggleTheme={toggleTheme} />
        <Routes>
          <Route path="/" element={<MainContent />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/roadmap" element={<Roadmap />} />
          {/* <Route path="/privacy" element={<Privacy />} /> */}
        </Routes>

        {/* Chat / Contact Form */}
        <div className="chat-section">
          <h3 className="chat-heading">Search movie by text</h3>
          <form onSubmit={handleChatSubmit} className="chat-form">
            <textarea
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              placeholder="Describe the movie..."
              className="chat-textarea"
              rows={2}
              required
            />
            <button type="submit" className="chat-submit-btn btn-3d" onClick={createRipple} disabled={loading}>
              {loading ? 'Processing...' : 'Search Movie'}
            </button>
            {chatSent && <p className="chat-sent-msg">✅ Email client opened!</p>}
          </form>
        </div>

        {/* <div style={{ "marginTop": "5rem" }}></div>
        <div className="bottom-strip">
          <div className="bottom-strip__slot">
            <iframe 
                title="video-slider"
                width="100%"
                height="100%"
                style={mediaStyle}
                // style={{ border: 'none', overflow: 'hidden', display: 'block' }}
                scrolling="no"
                sandbox="allow-scripts allow-same-origin allow-popups"
                src="/ad1.html"
              />
          </div>
          <div className="bottom-strip__slot">
            <iframe 
                title="video-slider"
                width="100%"
                height="100%"
                style={mediaStyle}
                // style={{ border: 'none', overflow: 'hidden', display: 'block' }}
                scrolling="no"
                sandbox="allow-scripts allow-same-origin allow-popups"
                src="/ad2.html"
              />
          </div>
          <div className="bottom-strip__slot">
            <iframe 
                title="video-slider"
                width="100%"
                height="100%"
                style={mediaStyle}
                // style={{ border: 'none', overflow: 'hidden', display: 'block' }}
                scrolling="no"
                sandbox="allow-scripts allow-same-origin allow-popups"
                src="/ad3.html"
              />
          </div>
          {/* <div className="bottom-strip__slot" />
          <div className="bottom-strip__slot" /> 
        </div> */}

        <div className='footer'>
          <div></div>
          <span>
            Leave feedback: <a href="mailto:letmeplzdoit@gmail.com">letmeplzdoit@gmail.com</a>
            {/* {' '}·{' '}
            <Link to="/privacy">Privacy Policy</Link> */}
          </span>
          <div className="support-links">
            <div style={{ position: 'relative' }}>
              <button
                className={supportBtnClasses}
                onClick={(e) => {
                  setShowSupportText(prev => ({ ...prev, patreon: true }));
                  handleCopy('4441111061042325', 'patreon');
                  setTimeout(() => {
                    document.querySelector('.kofi-button')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                  }, 50);
                }}
                style={{
                  backgroundColor: '#ff5252',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {showSupportText.patreon ? (
                  <img
                    src="/qr_cropped_3.png"
                    alt="QR Code"
                    className="qr-code-img-patreon"
                    style={{ minHeight: '390px', minWidth: '390px', objectFit: 'contain' }}
                    onLoad={() => document.querySelector('.kofi-button')?.scrollIntoView({ behavior: 'smooth', block: 'end' })}
                  />
                ) : 'Support'} {/* Support on Patreon */}
              </button>
              {/* {showTooltip.patreon && (
                <div style={{
                  position: 'absolute',
                  top: '-30px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#333',
                  color: '#fff',
                  padding: '5px 10px',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}>
                  Copied!
                </div>
              )} */}
            </div>
            <div style={{ position: 'relative' }}>
              <button
                className="support-button kofi-button btn-3d"
                onClick={() => {
                  setShowSupportText(prev => ({ ...prev, kofi: true }));
                  handleCopy('4441111061042325', 'kofi');
                }}
                style={{
                  backgroundColor: '#29abe0',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  // padding: '12px 28px',
                  fontWeight: '600',
                  // fontSize: '18px',
                  cursor: 'pointer'
                }}
              >
                {showSupportText.kofi ? (
                  <span style={{ userSelect: 'text' }}>4441111061042325</span>
                ) : 'Buy me a coffee'}
              </button>
              {showTooltip.kofi && (
                <div style={{
                  position: 'absolute',
                  top: '-30px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#333',
                  color: '#fff',
                  padding: '5px 10px',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}>
                  Copied!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Router>
  );
}

export default App;
